import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthPage } from './components/auth/AuthPage';
import { MagicLinkCallbackPage } from './pages/auth/MagicLinkCallbackPage';
import { PasswordResetPage } from './pages/auth/PasswordResetPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { HomePage } from './pages/HomePage';
import { CalendarPage } from './pages/CalendarPage';
import { MembersPage } from './pages/MembersPage';
import { SettingsPage } from './pages/SettingsPage';
import { EventDetailPage } from './pages/EventDetailPage';
import { CreateEventPage } from './pages/admin/CreateEventPage';
import { AnnouncementPage } from './pages/admin/AnnouncementPage';
import { LoadingSpinner } from './components/ui/LoadingSpinner';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-warm-50 dark:bg-gray-950 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  if (!isAdmin) return <Navigate to="/settings" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-50 dark:bg-gray-950 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={session ? <Navigate to="/" replace /> : <AuthPage />}
      />
      <Route
        path="/auth/callback"
        element={<MagicLinkCallbackPage />}
      />
      <Route
        path="/auth/reset-password"
        element={<PasswordResetPage />}
      />
      <Route
        path="/auth/forgot-password"
        element={session ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
      />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute><MembersPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/events/:id" element={<ProtectedRoute><EventDetailPage /></ProtectedRoute>} />

      {/* Create event — any authenticated member can schedule */}
      <Route
        path="/admin/create-event"
        element={<ProtectedRoute><CreateEventPage /></ProtectedRoute>}
      />
      <Route
        path="/admin/announcement"
        element={
          <ProtectedRoute>
            <AdminRoute>
              <AnnouncementPage />
            </AdminRoute>
          </ProtectedRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
